import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import { FLUSS_NODE_TYPES } from "modules/fluss/utils/bpmn";
import { FilterBuilder } from "modules/record/record-producer/components/record-producer-fields/condition-field/FilterBuilder";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface MetadataOption {
    id: string;
    name: string;
    label?: string;
    column_type?: string;
    code?: string;
}

const listRecords = async (tableName: string) => {
    const response = await new RecordManagement<MetadataOption>(tableName).orderBy("name", "asc").run();
    return response.data;
};

const bindingKindForType = (type: string) => {
    if (type === FLUSS_NODE_TYPES.userTask) return "assignment";
    if (type === FLUSS_NODE_TYPES.serviceTask || type === FLUSS_NODE_TYPES.sendTask) return "service";
    return "";
};

// A node may carry more than one fluss:Binding at once (e.g. an "assignment" binding plus an
// independent "notification" binding) — findBinding() resolves the primary one (assignment/
// approval/service/condition), explicitly excluding "notification" so the two never collide.
const findBinding = (businessObject: any) =>
    businessObject?.extensionElements?.values?.find(
        (value: any) => value.$type === "fluss:Binding" && value.kind !== "notification"
    );

const findNotificationBinding = (businessObject: any) =>
    businessObject?.extensionElements?.values?.find(
        (value: any) => value.$type === "fluss:Binding" && value.kind === "notification"
    );

const FlussAutomationPanel = ({
    selection,
    modeler,
    onChanged,
}: {
    selection: FlussElementSelection | null;
    modeler: any;
    onChanged: () => void;
}) => {
    const { t } = useTranslation();
    const [kind, setKind] = useState("");
    const [tableName, setTableName] = useState("");
    const [columnId, setColumnId] = useState("");
    const [businessRuleId, setBusinessRuleId] = useState("");
    const [assigneeRole, setAssigneeRole] = useState("");
    const [assigneeUser, setAssigneeUser] = useState("");
    const [assigneeExpression, setAssigneeExpression] = useState("");
    const [condition, setCondition] = useState("");
    const [isDefault, setIsDefault] = useState(false);
    const [notificationEnabled, setNotificationEnabled] = useState(false);
    const [templateCode, setTemplateCode] = useState("");

    const tablesQuery = useQuery({
        queryKey: ["fluss", "metadata", "tables"],
        queryFn: () => listRecords("sys_tables"),
    });
    const rulesQuery = useQuery({
        queryKey: ["fluss", "metadata", "business-rules"],
        queryFn: () => listRecords("sys_business_rules"),
    });
    const rolesQuery = useQuery({
        queryKey: ["fluss", "metadata", "roles"],
        queryFn: () => listRecords("sys_roles"),
    });
    const templatesQuery = useQuery({
        queryKey: ["fluss", "metadata", "email-templates"],
        queryFn: () => listRecords("sys_email_templates"),
    });
    const propertiesQuery = useQuery({
        queryKey: ["fluss", "metadata", "properties"],
        queryFn: () => listRecords("sys_properties"),
    });
    const selectedTable = tablesQuery.data?.find((table) => table.name === tableName);
    const columnsQuery = useQuery({
        queryKey: ["fluss", "metadata", "columns", selectedTable?.id],
        queryFn: async () => {
            const response = await new RecordManagement<MetadataOption>("sys_columns")
                .where("sys_table_id", selectedTable?.id || "")
                .orderBy("name", "asc")
                .run();
            return response.data;
        },
        enabled: Boolean(selectedTable?.id),
    });

    useEffect(() => {
        const businessObject = selection?.businessObject;
        const currentBinding = findBinding(businessObject);
        setKind(currentBinding?.kind || bindingKindForType(selection?.type || ""));
        setTableName(currentBinding?.table || "");
        setColumnId(currentBinding?.column || "");
        setBusinessRuleId(currentBinding?.businessRule || "");
        setAssigneeRole(currentBinding?.assigneeRole || "");
        setAssigneeUser(currentBinding?.assigneeUser || "");
        setAssigneeExpression(currentBinding?.assigneeExpression || "");
        setCondition(businessObject?.conditionExpression?.body || "");
        setIsDefault(Boolean(businessObject?.sourceRef?.default === businessObject));
        const notificationBinding = findNotificationBinding(businessObject);
        setNotificationEnabled(Boolean(notificationBinding));
        setTemplateCode(notificationBinding?.templateCode || "");
    }, [selection?.businessObject, selection?.id, selection?.type]);

    const propertyOptions = useMemo(
        () => (propertiesQuery.data || []).map((property) => `@property:${property.name}`),
        [propertiesQuery.data]
    );
    const conditionColumns = useMemo(() => {
        const recordRoot = selectedTable
            ? [{
                id: "__fluss_record",
                name: "record",
                label: t("fluss.automation.table", { defaultValue: "Anchor record" }),
                column_type: "REFERENCE",
                referenced_table_name: selectedTable.name,
            }]
            : [];
        const properties = (propertiesQuery.data || []).map((property) => ({
            id: `__fluss_property_${property.id}`,
            name: `@property:${property.name}`,
            label: `@property:${property.name}`,
            column_type: "TEXT",
            referenced_table_name: "",
        }));
        return [...recordRoot, ...properties] as any[];
    }, [propertiesQuery.data, selectedTable, t]);

    if (!selection) {
        return (
            <Alert severity="info">
                {t("fluss.automation.select_node", { defaultValue: "Select a node or connection to configure automation." })}
            </Alert>
        );
    }

    const modeling = modeler?.get("modeling");
    const moddle = modeler?.get("moddle");

    const updateName = (name: string) => {
        modeling?.updateProperties(selection.element, { name });
        onChanged();
    };

    const updateBinding = (updates: Record<string, string>) => {
        if (!modeling || !moddle) return;
        const businessObject = selection.businessObject;
        let extensionElements = businessObject.extensionElements;
        if (!extensionElements) {
            extensionElements = moddle.create("bpmn:ExtensionElements", { values: [] });
        }
        let target = findBinding(businessObject);
        if (!target) {
            target = moddle.create("fluss:Binding", { kind: kind || bindingKindForType(selection.type) });
            extensionElements.values = [...(extensionElements.values || []), target];
            target.$parent = extensionElements;
            modeling.updateProperties(selection.element, { extensionElements });
        }
        const normalized = Object.fromEntries(
            Object.entries(updates).map(([key, value]) => [key, value || undefined])
        );
        modeling.updateModdleProperties(selection.element, target, normalized);
        onChanged();
    };

    // Notification is an independent, additive binding (a user_task can have an assignment AND
    // a notification binding at once — see FR-NOT-01), so it's managed separately from the
    // primary assignment/approval/service binding above rather than sharing findBinding/target.
    const updateNotificationBinding = (updates: Record<string, string | undefined>) => {
        if (!modeling || !moddle) return;
        const businessObject = selection.businessObject;
        let extensionElements = businessObject.extensionElements;
        if (!extensionElements) {
            extensionElements = moddle.create("bpmn:ExtensionElements", { values: [] });
        }
        let target = findNotificationBinding(businessObject);
        if (!target) {
            target = moddle.create("fluss:Binding", { kind: "notification" });
            extensionElements.values = [...(extensionElements.values || []), target];
            target.$parent = extensionElements;
            modeling.updateProperties(selection.element, { extensionElements });
        }
        const normalized = Object.fromEntries(
            Object.entries(updates).map(([key, value]) => [key, value || undefined])
        );
        modeling.updateModdleProperties(selection.element, target, normalized);
        onChanged();
    };

    const removeNotificationBinding = () => {
        if (!modeling) return;
        const businessObject = selection.businessObject;
        const extensionElements = businessObject.extensionElements;
        const target = findNotificationBinding(businessObject);
        if (!extensionElements || !target) return;
        const values = (extensionElements.values || []).filter((value: any) => value !== target);
        modeling.updateModdleProperties(selection.element, extensionElements, { values });
        onChanged();
    };

    const updateCondition = (value: string) => {
        setCondition(value);
        const expression = value.trim()
            ? moddle.create("bpmn:FormalExpression", { body: value })
            : undefined;
        modeling?.updateProperties(selection.element, { conditionExpression: expression });
        onChanged();
    };

    const updateDefault = (checked: boolean) => {
        setIsDefault(checked);
        const sourceElement = modeler?.get("elementRegistry")?.get(selection.businessObject?.sourceRef?.id);
        if (sourceElement) {
            modeling?.updateProperties(sourceElement, { default: checked ? selection.businessObject : undefined });
            onChanged();
        }
    };

    const supportsBinding = [FLUSS_NODE_TYPES.userTask, FLUSS_NODE_TYPES.serviceTask, FLUSS_NODE_TYPES.sendTask].includes(selection.type as any);
    const isSequenceFlow = selection.type === "bpmn:SequenceFlow";

    return (
        <Stack gap={2}>
            <TextField
                size="small"
                label={t("fluss.automation.element_name", { defaultValue: "Element name" })}
                value={selection.businessObject?.name || ""}
                onChange={(event) => updateName(event.target.value)}
                fullWidth
            />

            {supportsBinding && (
                <>
                    <TextField
                        select
                        size="small"
                        label={t("fluss.automation.binding_type", { defaultValue: "Binding type" })}
                        value={kind}
                        onChange={(event) => {
                            setKind(event.target.value);
                            updateBinding({ kind: event.target.value });
                        }}
                    >
                        {selection.type === "bpmn:UserTask" && (
                            <MenuItem value="assignment">{t("fluss.binding.assignment", { defaultValue: "Assignment" })}</MenuItem>
                        )}
                        {selection.type === "bpmn:UserTask" && (
                            <MenuItem value="approval">{t("fluss.binding.approval", { defaultValue: "Approval" })}</MenuItem>
                        )}
                        {selection.type === "bpmn:ServiceTask" && (
                            <MenuItem value="service">{t("fluss.binding.service", { defaultValue: "Business rule" })}</MenuItem>
                        )}
                    </TextField>

                    {selection.type === "bpmn:UserTask" && (
                        <>
                            <Autocomplete
                                size="small"
                                options={rolesQuery.data || []}
                                getOptionLabel={(option) => option.label || option.name}
                                value={rolesQuery.data?.find((role) => role.name === assigneeRole) || null}
                                onChange={(_, value) => {
                                    const next = value?.name || "";
                                    setAssigneeRole(next);
                                    updateBinding({ assigneeRole: next });
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label={t("fluss.automation.assignee_role", { defaultValue: "Assignee role" })} />
                                )}
                            />
                            <TextField
                                size="small"
                                label={t("fluss.automation.assignee_user", { defaultValue: "Assignee user or var.*" })}
                                value={assigneeUser}
                                onChange={(event) => {
                                    setAssigneeUser(event.target.value);
                                    updateBinding({ assigneeUser: event.target.value });
                                }}
                            />
                            <TextField
                                size="small"
                                label={t("fluss.automation.assignee_expression", { defaultValue: "Assignee expression" })}
                                value={assigneeExpression}
                                onChange={(event) => {
                                    setAssigneeExpression(event.target.value);
                                    updateBinding({ assigneeExpression: event.target.value });
                                }}
                            />
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={notificationEnabled}
                                        onChange={(event) => {
                                            const checked = event.target.checked;
                                            setNotificationEnabled(checked);
                                            if (checked) {
                                                updateNotificationBinding({ templateCode });
                                            } else {
                                                setTemplateCode("");
                                                removeNotificationBinding();
                                            }
                                        }}
                                    />
                                )}
                                label={t("fluss.automation.notify_assignee", { defaultValue: "Notify assignee by email" })}
                            />
                            {notificationEnabled && (
                                <Autocomplete
                                    size="small"
                                    options={templatesQuery.data || []}
                                    getOptionLabel={(option) => t(String(option.label)) || option.name || option.code || ""}
                                    value={templatesQuery.data?.find((template) => template.code === templateCode) || null}
                                    onChange={(_, value) => {
                                        const next = value?.code || "";
                                        setTemplateCode(next);
                                        updateNotificationBinding({ templateCode: next });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t("fluss.automation.notification_template", { defaultValue: "Email template" })} />
                                    )}
                                />
                            )}
                        </>
                    )}

                    {selection.type === "bpmn:ServiceTask" && (
                        <Autocomplete
                            size="small"
                            options={rulesQuery.data || []}
                            getOptionLabel={(option) => t(String(option.label)) || option.name}
                            value={rulesQuery.data?.find((rule) => rule.id === businessRuleId) || null}
                            onChange={(_, value) => {
                                const next = value?.id || "";
                                setBusinessRuleId(next);
                                updateBinding({ businessRule: next });
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label={t("fluss.automation.business_rule", { defaultValue: "Business rule" })} />
                            )}
                        />
                    )}

                    <Autocomplete
                        size="small"
                        options={tablesQuery.data || []}
                        getOptionLabel={(option) => t(String(option.label)) || option.name}
                        value={tablesQuery.data?.find((table) => table.name === tableName) || null}
                        onChange={(_, value) => {
                            const next = value?.name || "";
                            setTableName(next);
                            setColumnId("");
                            updateBinding({ table: next, column: "" });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label={t("fluss.automation.table", { defaultValue: "Record table" })} />
                        )}
                    />
                    <Autocomplete
                        size="small"
                        options={columnsQuery.data || []}
                        getOptionLabel={(option) => t(String(option.label)) || option.name}
                        value={columnsQuery.data?.find((column) => column.id === columnId) || null}
                        onChange={(_, value) => {
                            const next = value?.id || "";
                            setColumnId(next);
                            updateBinding({ column: next });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label={t("fluss.automation.column", { defaultValue: "Record field" })} />
                        )}
                    />
                </>
            )}

            {isSequenceFlow && (
                <>
                    <Autocomplete
                        size="small"
                        options={tablesQuery.data || []}
                        getOptionLabel={(option) => t(String(option.label)) || option.name}
                        value={selectedTable || null}
                        onChange={(_, value) => {
                            const next = value?.name || "";
                            setTableName(next);
                            // Persisted on the edge itself (kind: "condition") purely so the
                            // choice round-trips on reselect — the compiler skips sequenceFlow
                            // entirely when scanning bindings, so this never reaches the runtime
                            // engine; it only re-seeds the FilterBuilder's record.* column list.
                            updateBinding({ table: next, kind: "condition" });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label={t("fluss.automation.table", { defaultValue: "Anchor table for conditions" })} />
                        )}
                    />
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t("fluss.automation.condition", { defaultValue: "UI Rules condition" })}
                        </Typography>
                        <FilterBuilder
                            value={condition}
                            columns={conditionColumns}
                            allColumns={(columnsQuery.data || []) as any[]}
                            tables={(tablesQuery.data || []) as any[]}
                            onChange={(value) => updateCondition(typeof value === "string" ? value : JSON.stringify(value))}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {t("fluss.automation.condition_help", {
                                defaultValue: "Use record.*, var.* or @property:* fields with the platform condition grammar.",
                            })}
                        </Typography>
                    </Box>
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={propertyOptions}
                        onChange={(_, value) => {
                            if (typeof value === "string") {
                                const suffix = Date.now().toString();
                                updateCondition(JSON.stringify({
                                    id: `fluss_property_${suffix}`,
                                    type: "logical",
                                    operator: "and",
                                    conditions: [{
                                        id: `fluss_property_condition_${suffix}`,
                                        type: "condition",
                                        field: value,
                                        operator: "eq",
                                        value: "",
                                    }],
                                }));
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label={t("fluss.automation.property_lookup", { defaultValue: "Property lookup" })} />
                        )}
                    />
                    <Autocomplete
                        size="small"
                        options={rulesQuery.data || []}
                        getOptionLabel={(option) => t(String(option.label)) || option.name}
                        onChange={(_, value) => value && updateCondition(JSON.stringify({ type: "business_rule", business_rule_id: value.id }))}
                        renderInput={(params) => (
                            <TextField {...params} label={t("fluss.automation.business_rule", { defaultValue: "Business rule" })} />
                        )}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={isDefault} onChange={(event) => updateDefault(event.target.checked)} />}
                        label={t("fluss.automation.default_flow", { defaultValue: "Default gateway flow" })}
                    />
                </>
            )}

            {!supportsBinding && !isSequenceFlow && (
                <Box>
                    <Typography color="text.secondary">
                        {t("fluss.automation.no_binding", { defaultValue: "This element has no automation binding in the MVP." })}
                    </Typography>
                </Box>
            )}
        </Stack>
    );
};

export default FlussAutomationPanel;
